import { useEffect, useState } from "react";
import {
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function EditPaper() {

    const navigate = useNavigate();

    const { id } = useParams();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        paper_type: "",

        paper: "",

        category: "",

        exam_date: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Categories
    |--------------------------------------------------------------------------
    */

    const [categories, setCategories] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] = useState(true);

    const [categoryLoading, setCategoryLoading] =
        useState(true);

    const [saving, setSaving] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | Fetch Paper Categories
    |--------------------------------------------------------------------------
    */

    const fetchCategories = async () => {

        try {

            setCategoryLoading(true);


            const response = await fetch(

                `${API_BASE_URL}/admin/paper-category`,

                {

                    method: "GET",

                    headers: {

                        "Accept":
                            "application/json"

                    },

                    credentials: "include"

                }

            );


            const data =
                await response.json();


            console.log(
                "Paper Category Response:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                setCategories(
                    data.data || []
                );

            } else {

                Swal.fire({

                    icon: "error",

                    title:
                        "Unable to Load Categories",

                    text:
                        data.message ||
                        "Unable to fetch paper categories.",

                    confirmButtonText:
                        "OK"

                });

            }


        } catch (error) {

            console.error(
                "Fetch Categories Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title:
                    "Server Error",

                text:
                    "Unable to connect with server.",

                confirmButtonText:
                    "OK"

            });

        } finally {

            setCategoryLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Existing Paper
    |--------------------------------------------------------------------------
    */

    const fetchPaper = async () => {

        try {

            setLoading(true);


            const response = await fetch(

                `${API_BASE_URL}/admin/papers/${id}`,

                {

                    method: "GET",

                    headers: {

                        "Accept":
                            "application/json"

                    },

                    credentials: "include"

                }

            );


            const data =
                await response.json();


            console.log(
                "Edit Paper Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (
                response.ok &&
                data.success
            ) {

                const paperData =
                    data.data;


                setFormData({

                    paper_type:
                        paperData.paper_type || "",

                    paper:
                        paperData.paper || "",

                    category:
                        paperData.category || "",

                    exam_date:
                        paperData.exam_date || ""

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Paper Not Found
            |--------------------------------------------------------------------------
            */

            Swal.fire({

                icon:
                    "error",

                title:
                    "Unable to Load Paper",

                text:
                    data.message ||
                    "Paper not found.",

                confirmButtonText:
                    "OK"

            }).then(() => {

                navigate("/papers");

            });


        } catch (error) {

            console.error(
                "Fetch Paper Error:",
                error
            );


            Swal.fire({

                icon:
                    "error",

                title:
                    "Server Error",

                text:
                    "Unable to load paper details.",

                confirmButtonText:
                    "OK"

            }).then(() => {

                navigate("/papers");

            });


        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Load Paper + Categories
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        if (!id) {

            Swal.fire({

                icon:
                    "error",

                title:
                    "Invalid Paper",

                text:
                    "Paper ID is missing.",

                confirmButtonText:
                    "OK"

            }).then(() => {

                navigate("/papers");

            });

            return;

        }


        fetchPaper();

        fetchCategories();

    }, [id]);


    /*
    |--------------------------------------------------------------------------
    | Handle Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData({

            ...formData,

            [name]: value

        });

    };


    /*
    |--------------------------------------------------------------------------
    | Reset Form
    |--------------------------------------------------------------------------
    */

    const handleReset = () => {

        fetchPaper();

    };


    /*
    |--------------------------------------------------------------------------
    | Submit / Update
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Prevent Multiple Submit
        |--------------------------------------------------------------------------
        */

        if (saving) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Paper Type Validation
        |--------------------------------------------------------------------------
        */

        if (
            !formData.paper_type.trim()
        ) {

            Swal.fire({

                icon:
                    "warning",

                title:
                    "Paper Type Required",

                text:
                    "Please select paper type.",

                confirmButtonText:
                    "OK"

            });

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Paper Validation
        |--------------------------------------------------------------------------
        */

        if (
            !formData.paper.trim()
        ) {

            Swal.fire({

                icon:
                    "warning",

                title:
                    "Paper Required",

                text:
                    "Please enter paper name.",

                confirmButtonText:
                    "OK"

            });

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Category Validation
        |--------------------------------------------------------------------------
        */

        if (
            !formData.category.trim()
        ) {

            Swal.fire({

                icon:
                    "warning",

                title:
                    "Category Required",

                text:
                    "Please select paper category.",

                confirmButtonText:
                    "OK"

            });

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Exam Date Validation
        |--------------------------------------------------------------------------
        */

        if (
            !formData.exam_date
        ) {

            Swal.fire({

                icon:
                    "warning",

                title:
                    "Exam Date Required",

                text:
                    "Please select exam date.",

                confirmButtonText:
                    "OK"

            });

            return;

        }


        try {

            setSaving(true);


            /*
            |--------------------------------------------------------------------------
            | PUT API
            |--------------------------------------------------------------------------
            */

            const response = await fetch(

                `${API_BASE_URL}/admin/papers/${id}`,

                {

                    method: "PUT",

                    headers: {

                        "Accept":
                            "application/json",

                        "Content-Type":
                            "application/json"

                    },

                    credentials: "include",

                    body: JSON.stringify({

                        paper_type:
                            formData.paper_type.trim(),

                        paper:
                            formData.paper.trim(),

                        category:
                            formData.category.trim(),

                        exam_date:
                            formData.exam_date

                    })

                }

            );


            const data =
                await response.json();


            console.log(
                "Update Paper Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (
                response.ok &&
                data.success
            ) {

                await Swal.fire({

                    icon:
                        "success",

                    title:
                        "Paper Updated!",

                    text:
                        data.message ||
                        "Paper updated successfully.",

                    confirmButtonText:
                        "OK",

                    confirmButtonColor:
                        "#198754"

                });


                navigate("/papers");

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Validation Error
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 422 &&
                data.errors
            ) {

                const errors =
                    Object.values(
                        data.errors
                    ).flat();


                Swal.fire({

                    icon:
                        "error",

                    title:
                        "Validation Error",

                    html:
                        errors
                            .map(
                                (error) =>
                                    `<div>${error}</div>`
                            )
                            .join(""),

                    confirmButtonText:
                        "OK"

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401 ||
                response.status === 403
            ) {

                Swal.fire({

                    icon:
                        "error",

                    title:
                        "Unauthorized",

                    text:
                        data.message ||
                        "You are not authorized to update paper.",

                    confirmButtonText:
                        "OK"

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Not Found
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 404
            ) {

                Swal.fire({

                    icon:
                        "error",

                    title:
                        "Paper Not Found",

                    text:
                        data.message ||
                        "The requested paper was not found.",

                    confirmButtonText:
                        "OK"

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Other Error
            |--------------------------------------------------------------------------
            */

            Swal.fire({

                icon:
                    "error",

                title:
                    "Unable to Update Paper",

                text:
                    data.message ||
                    "Something went wrong while updating paper.",

                confirmButtonText:
                    "OK"

            });


        } catch (error) {

            console.error(
                "Update Paper Error:",
                error
            );


            Swal.fire({

                icon:
                    "error",

                title:
                    "Server Error",

                text:
                    "Unable to connect with server. Please try again.",

                confirmButtonText:
                    "OK"

            });


        } finally {

            setSaving(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Initial Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div className="container-fluid">


                <div className="row justify-content-center">


                    <div className="col-lg-8">


                        <div className="card shadow-sm">


                            <div
                                className="
                                    card-header
                                    bg-primary
                                    text-white
                                "
                            >

                                <h4 className="mb-0">

                                    Edit Paper

                                </h4>

                            </div>


                            <div className="card-body">


                                <div
                                    className="
                                        d-flex
                                        justify-content-center
                                        align-items-center
                                    "
                                    style={{
                                        minHeight: "250px"
                                    }}
                                >

                                    <div className="text-center">


                                        <div
                                            className="
                                                spinner-border
                                                text-primary
                                            "
                                            role="status"
                                        >

                                            <span
                                                className="
                                                    visually-hidden
                                                "
                                            >
                                                Loading...
                                            </span>

                                        </div>


                                        <div className="mt-2">

                                            Loading paper...

                                        </div>


                                    </div>

                                </div>


                            </div>


                        </div>


                    </div>


                </div>


            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | JSX
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">


            <div className="row justify-content-center">


                <div className="col-lg-8">


                    <div className="card shadow-sm">


                        {/* ---------------------------------------------------
                            Card Header
                        --------------------------------------------------- */}

                        <div className="card-header bg-primary text-white">

                            <h4 className="mb-0">

                                Edit Paper

                            </h4>

                        </div>


                        {/* ---------------------------------------------------
                            Card Body
                        --------------------------------------------------- */}

                        <div className="card-body">


                            <form
                                onSubmit={
                                    handleSubmit
                                }
                            >


                                {/* ---------------------------------------------------
                                    Paper Type
                                --------------------------------------------------- */}

                                <div className="mb-3">


                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Paper Type

                                        <span className="text-danger">

                                            *

                                        </span>

                                    </label>


                                    <select
                                        className="form-select"
                                        name="paper_type"
                                        value={
                                            formData.paper_type
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        disabled={
                                            saving
                                        }
                                    >

                                        <option value="">

                                            Select Paper Type

                                        </option>


                                        <option value="Free">

                                            Free

                                        </option>


                                        <option value="Paid">

                                            Paid

                                        </option>


                                    </select>


                                </div>


                                {/* ---------------------------------------------------
                                    Paper
                                --------------------------------------------------- */}

                                <div className="mb-3">


                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Paper

                                        <span className="text-danger">

                                            *

                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        className="form-control"
                                        name="paper"
                                        placeholder="Enter Paper Name"
                                        value={
                                            formData.paper
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength="20"
                                        required
                                        disabled={
                                            saving
                                        }
                                    />


                                </div>


                                {/* ---------------------------------------------------
                                    Category
                                --------------------------------------------------- */}

                                <div className="mb-3">


                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Category

                                        <span className="text-danger">

                                            *

                                        </span>

                                    </label>


                                    <select
                                        className="form-select"
                                        name="category"
                                        value={
                                            formData.category
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        disabled={
                                            categoryLoading ||
                                            saving
                                        }
                                    >


                                        <option value="">

                                            {categoryLoading

                                                ? "Loading categories..."

                                                : "Select Paper Category"

                                            }

                                        </option>


                                        {categories.map(
                                            (item) => (

                                                <option
                                                    key={
                                                        item.id
                                                    }
                                                    value={
                                                        item.paper ||
                                                        item.paper_category
                                                    }
                                                >

                                                    {
                                                        item.paper ||
                                                        item.paper_category
                                                    }

                                                </option>

                                            )
                                        )}


                                    </select>


                                </div>


                                {/* ---------------------------------------------------
                                    Exam Date
                                --------------------------------------------------- */}

                                <div className="mb-4">


                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Exam Date

                                        <span className="text-danger">

                                            *

                                        </span>

                                    </label>


                                    <input
                                        type="date"
                                        className="form-control"
                                        name="exam_date"
                                        value={
                                            formData.exam_date
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        disabled={
                                            saving
                                        }
                                    />


                                </div>


                                {/* ---------------------------------------------------
                                    Buttons
                                --------------------------------------------------- */}

                                <div className="d-flex gap-2">


                                    {/* Update */}

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            saving
                                        }
                                    >

                                        {saving ? (

                                            <>

                                                <span
                                                    className="
                                                        spinner-border
                                                        spinner-border-sm
                                                        me-2
                                                    "
                                                    role="status"
                                                    aria-hidden="true"
                                                />

                                                Updating...

                                            </>

                                        ) : (

                                            "Update Paper"

                                        )}

                                    </button>


                                    {/* Reset */}

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={
                                            handleReset
                                        }
                                        disabled={
                                            saving
                                        }
                                    >

                                        Reset

                                    </button>


                                    {/* Back */}

                                    <button
                                        type="button"
                                        className="btn btn-outline-dark"
                                        onClick={() =>
                                            navigate(
                                                "/papers"
                                            )
                                        }
                                        disabled={
                                            saving
                                        }
                                    >

                                        Back

                                    </button>


                                </div>


                            </form>


                        </div>


                    </div>


                </div>


            </div>


        </div>

    );

}


export default EditPaper;